import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Observable } from "rxjs";
import swal from "sweetalert2";

import { FrcFirmado, TipoFrcFirmado } from "../../shared/model/frc/frc-firmado.model";
import { VacacionesService } from "../vacaciones.service";
import { PermisosService } from "../../permisos/permisos.service";
import { escapaHtml, fechaLegible, htmlHistorial } from "./historial-firmados";

/**
 * El FRC firmado a mano (30/09/2026).
 *
 * Quien escoge «Descargar para firmar» (al pedir o al autorizar) firma el
 * FRC-019 o el FRC-017 en papel. Este componente deja subir ese papel ya
 * firmado —escaneado en PDF o en foto JPG/PNG, hasta 10 MB— y verlo
 * después. Va junto al botón del FRC en cada pantalla que lo tiene.
 *
 * Solo aparece si alguna firma fue en papel (`enPapel`). Subir otro no
 * borra el anterior: el servidor lo conserva y, desde el 14/10/2026, con
 * más de uno «ver» abre el historial (se presenta el último; los demás
 * quedan como evidencia). Quién puede
 * subir y ver lo decide el servidor (la regla de abrir el FRC); aquí
 * `puedeSubir` solo dice si esta pantalla ofrece el botón.
 *
 * Desde el 15/10/2026 (queja de Irving: RH no veía el documento) quien
 * gestiona las vacaciones —el menú Seguimiento RH o quien administra el
 * Calendario— lo carga también donde no hubo papel (`gestiona`): el FRC
 * firmado o la evidencia de la solicitud, y ahí los textos dicen
 * «documento». A él «ver» le abre siempre el historial, y cada renglón
 * del historial se puede descargar.
 */
@Component({
  selector: "app-frc-firmado",
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: "./frc-firmado.component.html",
  styleUrls: ["./frc-firmado.component.css"],
})
export class FrcFirmadoComponent {
  /** VACACION (FRC-019) o PERMISO (FRC-017). */
  @Input() tipo: TipoFrcFirmado = "VACACION";

  /** id_vacacion o id_permiso. */
  @Input() id?: number | null;

  /** Alguna de las dos firmas fue en papel: solo entonces hay algo que subir. */
  @Input() enPapel = false;

  /** El vigente, si ya se subió. */
  @Input() firmado?: FrcFirmado | null;

  /** Si esta pantalla ofrece subirlo. La bandeja de quien resuelve solo lo muestra. */
  @Input() puedeSubir = true;

  /**
   * Quien gestiona (RH) puede cargar aunque no se haya firmado en papel
   * (15/10/2026). El servidor lo vuelve a decidir (gestionaDocumentos).
   */
  @Input() gestiona = false;

  /**
   * «boton»: el redondo de las tarjetas (app-form-btn). «mini»: el chico
   * de las bandejas y los detalles, con sus medidas en --frc-alto,
   * --frc-radio, --frc-letra y --frc-relleno. «icono»: el de las tablas.
   */
  @Input() variante: "boton" | "mini" | "icono" = "boton";

  /** Se subió uno: la pantalla lo guarda en su renglón. */
  @Output() cambio = new EventEmitter<FrcFirmado>();

  @ViewChild("archivo") archivo?: ElementRef<HTMLInputElement>;

  subiendo = false;
  abriendo = false;

  /** Lo mismo que acepta el servidor (ArchivoFirmado.java). */
  private static readonly EXTENSIONES = ["pdf", "jpg", "jpeg", "png"];
  private static readonly MAXIMO = 10 * 1024 * 1024;

  constructor(
    private vacaciones: VacacionesService,
    private permisos: PermisosService,
    private cdr: ChangeDetectorRef,
  ) {}

  /** Cómo se llama el formato en los textos. */
  get doc(): string {
    return this.tipo === "PERMISO" ? "FRC-017" : "FRC-019";
  }

  get haySubido(): boolean {
    return !!this.firmado?.idFirmado;
  }

  /** Se ofrece subir: con firma en papel, como siempre, o a quien gestiona aunque no la haya. */
  get ofreceSubir(): boolean {
    return this.puedeSubir && (this.enPapel || this.gestiona);
  }

  /**
   * RH carga donde no hubo papel: lo que sube puede ser la evidencia y no
   * el FRC firmado, así que se habla de «documento». Con papel, los textos
   * de siempre.
   */
  get porDocumento(): boolean {
    return this.gestiona && !this.enPapel;
  }

  /** Cómo se nombra en los avisos: «FRC-019 firmado» o «documento». */
  get que(): string {
    return this.porDocumento ? "documento" : this.doc + " firmado";
  }

  /** Lo mismo con mayúscula, para el botón y el título del historial. */
  get queTitulo(): string {
    return this.que.charAt(0).toUpperCase() + this.que.slice(1);
  }

  /** Las clases del botón con texto, según la variante. */
  get claseBoton(): string {
    return this.variante === "mini"
      ? "frc-mini"
      : "app-form-btn app-form-btn--secondary app-form-btn--compact";
  }

  /** Lo que dice el tooltip del firmado: cuándo y quién lo subió. */
  get detalle(): string {
    const f = this.firmado;
    if (!f) {
      return "";
    }
    let texto = "Ver el " + this.que;
    if (f.fecha) {
      texto += " · subido el " + fechaLegible(f.fecha);
    }
    if (f.usuarioNombre) {
      texto += " por " + f.usuarioNombre;
    }
    if (this.versiones > 1) {
      texto += " · " + this.versiones + " subidos: toca para ver el historial";
    } else if (this.gestiona) {
      texto += " · toca para ver el historial";
    }
    return texto;
  }

  get textoSubir(): string {
    if (this.porDocumento) {
      return "Carga el " + this.doc + " firmado o la evidencia de esta solicitud (PDF, JPG o PNG); " +
        "lo anterior queda en el historial";
    }
    return this.haySubido
      ? "Sube otro escaneo o foto: se presenta el nuevo y el anterior queda en el historial"
      : "Sube el " + this.doc + " firmado a mano, escaneado en PDF o en foto JPG o PNG";
  }

  /** El texto del botón de subir (variantes con texto). */
  get etiquetaSubir(): string {
    if (this.subiendo) {
      return "Subiendo…";
    }
    if (this.haySubido) {
      return "Subir otro";
    }
    return this.porDocumento ? "Subir documento" : "Subir firmado";
  }

  /** Lo que lee el lector de pantalla en el ícono de subir. */
  get ariaSubir(): string {
    return this.haySubido ? "Subir otro " + this.que : "Subir el " + this.que;
  }

  /** El texto del botón de ver (variantes con texto): cuántos van, si más de uno. */
  get etiquetaVer(): string {
    if (this.abriendo) {
      return "Abriendo…";
    }
    return this.versiones > 1 ? this.queTitulo + " (" + this.versiones + ")" : this.queTitulo;
  }

  /* ================= Subir ================= */

  elegir(): void {
    if (!this.subiendo) {
      this.archivo?.nativeElement.click();
    }
  }

  elegido(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    // Se limpia ya: si escoge el mismo archivo otra vez, vuelve a avisar.
    input.value = "";
    if (!archivo || this.id == null) {
      return;
    }
    const ext = (archivo.name.split(".").pop() || "").toLowerCase();
    if (!FrcFirmadoComponent.EXTENSIONES.includes(ext)) {
      this.aviso("Sube el " + this.que + " en PDF, JPG o PNG.", "error");
      return;
    }
    if (archivo.size === 0) {
      this.aviso("El archivo está vacío.", "error");
      return;
    }
    if (archivo.size > FrcFirmadoComponent.MAXIMO) {
      this.aviso("El archivo pesa más de 10 MB. Escanéalo con menos resolución o súbelo en foto.", "error");
      return;
    }
    if (!this.haySubido) {
      this.subir(archivo);
      return;
    }
    swal
      .fire({
        title: "¿Subir otro " + this.que + "?",
        html:
          `<div style="text-align:left;line-height:1.6;font-size:15px;color:#253042">` +
          `Se presentará el nuevo, <b>${escapaHtml(archivo.name)}</b>. ` +
          `El que ya estaba no se borra: queda en el historial, donde se puede ver.</div>`,
        icon: "question",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Sí, subir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b71c1c",
      })
      .then((r) => {
        if (r.isConfirmed) {
          this.subir(archivo);
        }
      });
  }

  private subir(archivo: File): void {
    this.subiendo = true;
    this.cdr.detectChanges();
    const peticion: Observable<{ firmado?: FrcFirmado; codigoError?: string; mensajeError?: string }> =
      this.tipo === "PERMISO"
        ? this.permisos.subirFirmado(this.id!, archivo)
        : this.vacaciones.subirFirmado(this.id!, archivo);
    peticion.subscribe({
      next: (res) => {
        this.subiendo = false;
        if (res.codigoError || !res.firmado) {
          const falla = this.porDocumento ? "No se pudo subir el documento." : "No se pudo subir el formato firmado.";
          this.aviso(res.mensajeError || falla, "error");
          this.cdr.detectChanges();
          return;
        }
        this.firmado = res.firmado;
        this.cambio.emit(res.firmado);
        this.aviso("Quedó guardado el " + this.que + ".", "success");
        this.cdr.detectChanges();
      },
      error: () => {
        this.subiendo = false;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Ver ================= */

  /**
   * Lo abre en otra pestaña. La pestaña se abre YA, en el clic: si se
   * abriera cuando llega el archivo, el navegador la tomaría por ventana
   * emergente y la bloquearía. Si aun así no se pudo, se descarga.
   */
  ver(): void {
    // A quien gestiona, siempre el historial aunque haya uno solo: RH
    // audita quién lo subió y cuándo, y desde ahí lo descarga (15/10/2026).
    if (this.gestiona || this.versiones > 1) {
      this.historial();
      return;
    }
    this.abre();
  }

  /** Cuántos se han subido; sin el dato (servidor anterior), uno. */
  get versiones(): number {
    return this.firmado?.versiones ?? 1;
  }

  /** Abre el que se presenta o, con su id, uno del historial. */
  private abre(idFirmado?: number, nombre?: string): void {
    if (this.id == null || this.abriendo) {
      return;
    }
    const pestana = window.open("", "_blank");
    this.abriendo = true;
    this.cdr.detectChanges();
    this.pideArchivo(this.id, idFirmado).subscribe({
      next: (blob) => {
        this.abriendo = false;
        const url = URL.createObjectURL(blob);
        if (pestana && !pestana.closed) {
          pestana.location.href = url;
          // La pestaña ya lo cargó; el enlace se suelta después.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
          this.guarda(url, nombre);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.abriendo = false;
        pestana?.close();
        this.aviso("No se pudo abrir el " + this.que + ".", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Baja uno del historial con el nombre con que se subió (15/10/2026),
   * sin abrir pestaña: RH guarda la evidencia. Sirve a todos, no solo a RH.
   */
  private descarga(idFirmado: number, nombre?: string): void {
    if (this.id == null || this.abriendo) {
      return;
    }
    this.abriendo = true;
    this.cdr.detectChanges();
    this.pideArchivo(this.id, idFirmado).subscribe({
      next: (blob) => {
        this.abriendo = false;
        this.guarda(URL.createObjectURL(blob), nombre);
        this.cdr.detectChanges();
      },
      error: () => {
        this.abriendo = false;
        this.aviso("No se pudo descargar el " + this.que + ".", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Los bytes del que se presenta o, con su id, de uno del historial. */
  private pideArchivo(id: number, idFirmado?: number): Observable<Blob> {
    return this.tipo === "PERMISO"
      ? this.permisos.verFirmado(id, idFirmado)
      : this.vacaciones.verFirmado(id, idFirmado);
  }

  /** Lo descarga con su nombre. El enlace se suelta después: el navegador ya lo tomó. */
  private guarda(url: string, nombre?: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre || this.firmado?.nombreArchivo || this.doc + "-firmado";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  /* ================= Historial (14/10/2026) =================
     Cada vez que alguien sube el formato firmado, el anterior no se
     borra: queda como evidencia. Aquí se ven todos y se abre cualquiera;
     el que se presenta es el último. Desde el 15/10/2026 cada uno también
     se descarga con su nombre. */

  historial(): void {
    if (this.id == null || this.abriendo) {
      return;
    }
    this.abriendo = true;
    this.cdr.detectChanges();
    const peticion: Observable<{ firmados?: FrcFirmado[]; codigoError?: string; mensajeError?: string }> =
      this.tipo === "PERMISO" ? this.permisos.historialFirmado(this.id) : this.vacaciones.historialFirmado(this.id);
    peticion.subscribe({
      next: (res) => {
        this.abriendo = false;
        this.cdr.detectChanges();
        const firmados = res.firmados || [];
        if (res.codigoError || !firmados.length) {
          this.aviso(res.mensajeError || "No se pudo consultar el historial del " + this.que + ".", "error");
          return;
        }
        // Al descargar (o si la pestaña no se abrió) cada uno se guarda con su nombre.
        const nombre = (idFirmado: number) => firmados.find((f) => f.idFirmado === idFirmado)?.nombreArchivo;
        swal.fire({
          title: this.queTitulo + " · historial",
          html: htmlHistorial(firmados),
          showConfirmButton: false,
          showCloseButton: true,
          width: 600,
          didOpen: (ventana) => {
            this.enlaza(ventana, "firmado", (idFirmado) => this.abre(idFirmado, nombre(idFirmado)));
            this.enlaza(ventana, "descargar", (idFirmado) => this.descarga(idFirmado, nombre(idFirmado)));
          },
        });
      },
      error: () => {
        this.abriendo = false;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Apoyo ================= */

  /** Cada botón del historial con su acción; el id del archivo va en data-firmado o data-descargar. */
  private enlaza(ventana: HTMLElement, dato: "firmado" | "descargar", accion: (idFirmado: number) => void): void {
    ventana.querySelectorAll<HTMLButtonElement>(`[data-${dato}]`).forEach((boton) =>
      boton.addEventListener("click", () => accion(Number(boton.dataset[dato]))),
    );
  }

  private aviso(texto: string, icono: "success" | "error"): void {
    swal.fire({
      text: texto,
      icon: icono,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#b71c1c",
    });
  }
}
